import { PreloadConfigService } from '@common/config/preload-config.service';
import { inject, Injectable, Signal, computed } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, merge, startWith } from 'rxjs';

export interface CurrentRoute { // Datos del Navegador (Fuera de Angular)
  // URL Completa del navegador
  fullUrl: string;                                      // "https://sub.app.dominio.com/base/angular/ruta?id=1"

  protocol: string;                                     // https:
  host:     string;                                     // sub.dominio.com
  domain: string;                                       // "dominio.com"
  subDomainsArr: string[];                              // ["sub", "app"]

  // Rutas Externas (Base)
  basePath: string;                                     // /gestion/mi-app (lo que está antes de Angular)
  basePathArr: string[];                                // ["gestion", "clientes"]

  // Datos de Angular
  fullRoute:      string;                               // "/users/123?tab=bio"
  relativeRoute:  string[];                             // ["users", "123"]
  params:         Record<string, string | undefined>;   // { id: "123", tab: "bio" }
}

@Injectable({
  providedIn: 'root'
})
export class HelperRoutingService {

  private readonly router =         inject(Router) ;
  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly configJSON = inject(PreloadConfigService);
  apiURL = computed(() => this.configJSON.config()?.apiUrl);
  // proneApiURL = computed(() => this.configJSON.config()?.apiProneUrl);

  // ? ######################################
  // ? ############ ROUTE PARAMS ############
  // ? ######################################

  // Se define el Signal con una sola fuente y la opción initialValue obligatoria
  public currentRoute: Signal<CurrentRoute> = toSignal(
    merge(
      this.router.events.pipe(filter(e => e instanceof NavigationEnd)),
      this.activatedRoute.queryParams,
      this.activatedRoute.params
    ).pipe(
      map(() => this.getSnapshot())
    ),
    { initialValue: this.getSnapshot() }
  );

  private getSnapshot(): CurrentRoute {
    const { protocol, host, hostname, pathname, href } = window.location;

    const hostParts = hostname.split('.');
    const domain = hostParts.slice(-2).join('.');
    const subDomainsArr = hostParts.length > 2 ? hostParts.slice(0, -2) : [];

    const angularPath = this.router.url.split('?')[0];
    const pathnameArr = pathname.split('/').filter(s => !!s);

    let basePathArr: string[] = [];
    const angularPathArr = angularPath.split('/').filter(s => !!s);

    if (angularPathArr.length > 0 && pathnameArr.length >= angularPathArr.length) {
      const pathnameTail = pathnameArr.slice(-angularPathArr.length);
      const matchesTail = angularPathArr.every((seg, index) => seg === pathnameTail[index]);
      if (matchesTail) {
        basePathArr = pathnameArr.slice(0, pathnameArr.length - angularPathArr.length);
      }
    }

    if (basePathArr.length < 1) {
      const availableLangs = ['es', 'en', 'ca', 'gl', 'eu'];
      const langIndex = pathnameArr.findIndex((seg) => availableLangs.includes(seg));
      if (langIndex >= 0) {
        basePathArr = pathnameArr.slice(0, langIndex + 1);
      }
    }

    const basePath = basePathArr.length > 0 ? '/' + basePathArr.join('/') : '';

    const urlTree = this.router.parseUrl(this.router.url);

    return {
      fullUrl: href, // Captura la URL completa con protocolo, dominio, path y queryParams
      protocol,
      host,
      domain,
      subDomainsArr,
      basePath,
      basePathArr,
      fullRoute: this.router.url,
      relativeRoute: urlTree.root.children['primary']?.segments.map(s => s.path) ?? [],
      params: {
        ...this.activatedRoute.snapshot.queryParams,
        ...this.activatedRoute.snapshot.params,
        ...this.activatedRoute.snapshot.firstChild?.params
      }
    };
  }

  availableLangs = ['es', 'en', 'ca', 'gl', 'eu']; //! sería adecuado mover esto a un lugar mas centralizado, como settings o toml.
  urlPathWithoutLang:string = '';

  public readonly currentLangFromRoute = computed( () => {
    const routeObj = this.currentRoute()
    const basePath = structuredClone(routeObj.basePathArr);
    let basePathWithoutLang = [];
    if (!basePath || basePath.length < 1) {
      return undefined;
    };
    const langMaybe = basePath[basePath.length - 1];
    const langFound = this.availableLangs.find( (el) => el === langMaybe )
    if (langFound){
      basePath.pop()
    };
    basePathWithoutLang = basePath;
    let basePathString = ''
    for (const el of basePath) {
      basePathString += '/' + el
    }
    this.urlPathWithoutLang = routeObj.protocol + '//' + routeObj.host + basePathString;
    return langFound
  });




  // ? ######################################
  // ? ############# NAVIGATION #############
  // ? ######################################
  public navigateFullPath(pathArr:string[]){
    this.router.navigate(pathArr)
  }

  public navigateToSiblings(path:string){
    let lastChildRoute = this.getLastActivatedRouteInChain(this.activatedRoute);
    this.router.navigate(['../' + path], { relativeTo: lastChildRoute })
  }
  private readonly getLastActivatedRouteInChain = (activatedRoute: ActivatedRoute): ActivatedRoute => {
    // Esta funcion permite utilizar router.navigate con rutas relativas (navigate to siblings)
    // en lugar de absolutas
    let lastRoute = activatedRoute;
    while (lastRoute.firstChild) {
      lastRoute = lastRoute.firstChild;
    }
    return lastRoute;
  };

  public navigateToLanguaje(requiredLanguage:string, token:string, path:''|'/prone-operations'|'/login'):void {
    const langFound = this.availableLangs.find( (el) => el === requiredLanguage )
    if( !langFound || this.currentLangFromRoute() == langFound  ){
      return
    }
    const url = this.urlPathWithoutLang + '/' + langFound + path + '/?token=' + token;
    this.navigateWWW(url);
  }

  /**
   * Navega a una URL externa fuera del proyecto Angular.
   * @param url URL completa (ej. 'https://google.com') o relativa al servidor.
   * @param target '_self' para abrir en la misma pestaña, '_blank' para una nueva.
   */
  public navigateWWW( url: string, target: '_self' | '_blank' = '_self'): void {
    console.log('navigating TO: ', url);
    if (target === '_blank') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = url;
    }
  }




}
